import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CalendarOff, ContactRound, Link2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const field =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50";
const labelCls = "text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45";

const fmtDay = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

function SocialCard() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/api/content/social")
            .then((res) => setForm(res.data))
            .catch((e) => toast.error(apiError(e, "Failed to load social links")));
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/api/admin/content/social", form);
            setForm(data);
            toast.success("Social links updated — footer pe icons live ho gaye");
        } catch (e) {
            toast.error(apiError(e, "Failed to save social links"));
        } finally {
            setBusy(false);
        }
    };

    if (!form) return null;

    return (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-7" data-testid="social-links-card">
            <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-purple">
                    <Link2 size={20} strokeWidth={2.2} />
                </span>
                <div>
                    <h3 className="text-base font-bold tracking-tight text-ink">Social Links</h3>
                    <p className="text-xs font-medium text-ink/45">URL daalte hi footer mein icon dikhne lagega — khaali chhodne par icon chhupa rahega</p>
                </div>
            </div>
            <div className="mt-6 space-y-4">
                {[
                    { key: "instagram", label: "Instagram URL" },
                    { key: "linkedin", label: "LinkedIn URL" },
                    { key: "facebook", label: "Facebook URL" },
                ].map((s) => (
                    <div key={s.key}>
                        <label className={labelCls}>{s.label}</label>
                        <input
                            data-testid={`social-${s.key}`}
                            value={form[s.key]}
                            onChange={set(s.key)}
                            className={field}
                            placeholder={`https://${s.key}.com/...`}
                        />
                    </div>
                ))}
            </div>
            <button
                data-testid="social-save-btn"
                onClick={save}
                disabled={busy}
                className="btn-gradient mt-6 rounded-full px-7 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
                {busy ? "Saving…" : "Save & Publish"}
            </button>
        </div>
    );
}

function BlockedDatesCard() {
    const [dates, setDates] = useState(null);
    const [newDate, setNewDate] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/admin/blocked-dates");
            setDates(data.dates);
        } catch (e) {
            toast.error(apiError(e, "Failed to load blocked dates"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const todayIso = new Date().toISOString().slice(0, 10);

    const add = async () => {
        if (!newDate) return toast.error("Pehle ek date choose karein");
        setBusy(true);
        try {
            await api.post("/api/admin/blocked-dates", { date: newDate });
            setNewDate("");
            await load();
            toast.success(`${fmtDay(newDate)} ab demo bookings ke liye closed hai`);
        } catch (e) {
            toast.error(apiError(e, "Failed to block date"));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (date) => {
        try {
            await api.delete(`/api/admin/blocked-dates/${date}`);
            await load();
            toast.success(`${fmtDay(date)} wapas open ho gaya`);
        } catch (e) {
            toast.error(apiError(e, "Failed to remove date"));
        }
    };

    if (!dates) return null;

    return (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-7" data-testid="blocked-dates-card">
            <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-blue">
                    <CalendarOff size={20} strokeWidth={2.2} />
                </span>
                <div>
                    <h3 className="text-base font-bold tracking-tight text-ink">Blocked Dates (Holidays)</h3>
                    <p className="text-xs font-medium text-ink/45">In dates pe demo book nahi hoga — website pe "Closed" dikhega</p>
                </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
                <input
                    data-testid="blocked-date-input"
                    type="date"
                    min={todayIso}
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className={`${field} mt-0 max-w-[200px]`}
                />
                <button
                    data-testid="blocked-date-add-btn"
                    onClick={add}
                    disabled={busy}
                    className="btn-gradient inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-bold text-white disabled:opacity-50"
                >
                    <Plus size={14} strokeWidth={2.6} />
                    Block Date
                </button>
            </div>

            {dates.length === 0 ? (
                <p className="mt-5 text-sm font-medium text-ink/45" data-testid="blocked-dates-empty">
                    Koi date blocked nahi hai — saare din demos ke liye open hain.
                </p>
            ) : (
                <div className="mt-5 flex flex-wrap gap-2.5" data-testid="blocked-dates-list">
                    {dates.map((d) => (
                        <span
                            key={d}
                            data-testid={`blocked-date-${d}`}
                            className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-[13px] font-bold text-rose-700"
                        >
                            {fmtDay(d)}
                            <button
                                data-testid={`blocked-date-remove-${d}`}
                                onClick={() => remove(d)}
                                aria-label={`Unblock ${fmtDay(d)}`}
                                className="flex h-5 w-5 items-center justify-center rounded-full bg-white/80 text-rose-500 transition-colors hover:text-rose-700"
                            >
                                <X size={12} strokeWidth={3} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function ContactCard() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/api/content/contact")
            .then((res) => setForm(res.data))
            .catch((e) => toast.error(apiError(e, "Failed to load contact info")));
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form.name.trim() || !form.address.trim()) return toast.error("Name aur address required hain");
        setBusy(true);
        try {
            const { data } = await api.put("/api/admin/content/contact", {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                address: form.address.trim(),
            });
            setForm(data);
            toast.success("Contact info updated");
        } catch (e) {
            toast.error(apiError(e, "Failed to save contact info"));
        } finally {
            setBusy(false);
        }
    };

    if (!form) return null;

    return (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-7" data-testid="contact-info-card">
            <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-teal">
                    <ContactRound size={20} strokeWidth={2.2} />
                </span>
                <div>
                    <h3 className="text-base font-bold tracking-tight text-ink">Contact Info</h3>
                    <p className="text-xs font-medium text-ink/45">
                        Phone, email aur address — ek baar badlo, navbar, footer, map, Call aur WhatsApp sab jagah update
                    </p>
                </div>
            </div>

            <div className="mt-6 space-y-4">
                <div>
                    <label className={labelCls}>Business Name</label>
                    <input data-testid="contact-name" value={form.name} onChange={set("name")} className={field} maxLength={60} />
                </div>
                <div>
                    <label className={labelCls}>Mobile Number (10 digit — Call & WhatsApp dono isi pe jayenge)</label>
                    <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={field} maxLength={15} placeholder="8586860979" />
                </div>
                <div>
                    <label className={labelCls}>Email</label>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={field} maxLength={80} />
                </div>
                <div>
                    <label className={labelCls}>Address (Google Maps bhi isi se update hoga)</label>
                    <textarea data-testid="contact-address" value={form.address} onChange={set("address")} rows={2} maxLength={200} className={`${field} resize-none`} />
                </div>
            </div>

            <button
                data-testid="contact-save-btn"
                onClick={save}
                disabled={busy}
                className="btn-gradient mt-6 rounded-full px-7 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
                {busy ? "Saving…" : "Save & Publish"}
            </button>
        </div>
    );
}

export default function SettingsTab() {
    const [maxDemos, setMaxDemos] = useState(null);
    const [input, setInput] = useState("5");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/admin/settings");
            setMaxDemos(data.max_demos_per_day);
            setInput(String(data.max_demos_per_day));
        } catch (e) {
            toast.error(apiError(e, "Failed to load settings"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        const value = parseInt(input, 10);
        if (!Number.isInteger(value) || value < 1 || value > 50) {
            return toast.error("Limit 1 se 50 ke beech honi chahiye");
        }
        setBusy(true);
        try {
            const { data } = await api.put("/api/admin/settings", { max_demos_per_day: value });
            setMaxDemos(data.max_demos_per_day);
            toast.success(`Demo limit updated — ab har din ${data.max_demos_per_day} demos allowed hain`);
        } catch (e) {
            toast.error(apiError(e, "Failed to save settings"));
        } finally {
            setBusy(false);
        }
    };

    if (maxDemos === null) {
        return (
            <div className="flex justify-center py-20" data-testid="settings-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    return (
        <div data-testid="settings-tab" className="max-w-xl space-y-6">
            <ContactCard />
            <SocialCard />
            <BlockedDatesCard />

            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-7">
                <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-purple/10 via-brand-blue/10 to-brand-teal/10 text-brand-blue">
                        <CalendarClock size={20} strokeWidth={2.2} />
                    </span>
                    <div>
                        <h3 className="text-base font-bold tracking-tight text-ink">Demo Slots Per Day</h3>
                        <p className="text-xs font-medium text-ink/45">Har din kitne free demos allow karne hain</p>
                    </div>
                </div>

                <div className="mt-6 flex items-center gap-4">
                    <input
                        data-testid="demo-limit-input"
                        type="number"
                        min={1}
                        max={50}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        className="w-28 rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-2xl font-bold text-ink outline-none transition-colors focus:border-brand-blue/50"
                    />
                    <div className="text-sm font-medium leading-snug text-ink/55">
                        demos per day
                        <p className="mt-0.5 text-xs text-ink/40">
                            Currently: <span className="font-bold text-ink/70">{maxDemos}</span> — website ki availability isi hisaab se dikhti hai
                        </p>
                    </div>
                </div>

                <button
                    data-testid="settings-save-btn"
                    onClick={save}
                    disabled={busy || parseInt(input, 10) === maxDemos}
                    className="btn-gradient mt-6 rounded-full px-7 py-3 text-sm font-bold text-white disabled:opacity-50"
                >
                    {busy ? "Saving…" : "Save Limit"}
                </button>

                <p className="mt-4 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 text-xs font-medium leading-relaxed text-ink/50">
                    Note: Limit kam karne par bhi pehle se booked demos cancel nahi hote — sirf nayi bookings rukengi jab din ka count limit tak pahunch jaye.
                </p>
            </div>
        </div>
    );
}
